import API, { APIResponse } from '../../../lib/commons/api'
import { IItemDelta } from '../../../domain/value-object/ItemDeltaInputVO'
import { SlackUser } from '../../../domain/value-object/Slack/UserVO'
import { DivisionDto } from '../divisions'

export type UserDto = {
  uuid: string
  name: string
  code: string
  iconUrl: string
}

export type RoleDto = {
  uuid: string
  code: string
  name: string
}

export type PermissionPolicyDto = {
  uuid: string
  code: string
  name: string
}

// ユーザー情報画面が参照する項目のみを宣言した、バックエンド UserDetailDto の
// 部分集合。日付は ISO 文字列、日時は UTC のエポックミリ秒で返る。
export type UserDetailDto = {
  uuid: string
  code?: string
  // メールアドレスは UpdateUserDelta.Input に無く更新できないため、表示専用。
  // 変更は /api/v1/users/attempt_change_email を経由する。
  email: string
  name: string
  nameAlphabet?: string
  nameKana?: string
  iconUrl?: string
  phoneNumber?: string
  githubUserName?: string
  birthday?: string
  hireDate?: string
  leaveDate?: string
  validFrom?: number
  validTo?: number
  timezoneId?: string
  emailNotification: boolean
  division?: DivisionDto
  role?: RoleDto
  // 登録ステータスは UpdateUserDelta.Input に無く更新できないため、表示専用。
  registrationStatus?: string
  // 本登録URLの有効期限。UpdateUserDelta.Input に無く更新できないため、表示専用。
  oneTimeTokenExpiredAt?: number
  partnerMember: boolean
  slackUser?: SlackUser[]
  permissionPolicies?: PermissionPolicyDto[]
}

// /me 画面で編集できる項目のみを、バックエンドの UpdateUserDelta.Input と同じ
// フィールド名で宣言する。uuid は /me/delta では認証情報から上書きされるため送らない。
export type MeUpdateDeltaInput = {
  name?: IItemDelta<string>
  nameAlphabet?: IItemDelta<string>
  nameKana?: IItemDelta<string>
  iconUrl?: IItemDelta<string>
  phoneNumber?: IItemDelta<string>
  githubUserName?: IItemDelta<string>
  birthday?: IItemDelta<string>
  timezoneId?: IItemDelta<string>
  emailNotification?: IItemDelta<boolean>
  // Input のフィールド名は単数形。lib/functions/user の slackUsers とは異なる。
  slackUser?: IItemDelta<SlackUser[]>
}

// Fetch-all uses a high limit so every record comes back in a single request.
const FIND_ALL_LIMIT = 2000

export const findUsers = async (): Promise<UserDto[]> => {
  const response = await API.functional.request(
    'GET',
    '/api/v1/users/find',
    { validOnly: false, limit: FIND_ALL_LIMIT },
    true
  )
  return (response.json?.data ?? []) as UserDto[]
}

// 接続失敗時、api.tsのfetchラッパーは通知を出したうえで空の成功応答（json: {}）を
// 返す。空のユーザーとして扱わず、かつ通知を重ねないよう、例外ではなく undefined を返す。
export const fetchMe = async (): Promise<UserDetailDto | undefined> => {
  const response = await API.functional.request('GET', '/api/v1/users/me')
  if (!response.json?.uuid) return undefined
  return response.json as UserDetailDto
}

// 更新は 200 レスポンスにワーニング・エラーメッセージが含まれることがあるため、
// 呼び出し元で判定できるようレスポンスをそのまま返す。接続失敗時は取得と同じ理由で
// undefined を返す。
export const updateMeDelta = async (
  input: MeUpdateDeltaInput
): Promise<APIResponse | undefined> => {
  const response = await API.functional.request(
    'PUT',
    '/api/v1/users/me/delta',
    input
  )
  if (!response.hasWarning && !response.hasError && !response.json?.uuid) {
    return undefined
  }
  return response
}
